import React from "react";

/**
 * ======================================================================
 * PolitiqueConfidentialiteView.jsx
 * ======================================================================
 *
 * RÔLE
 * ----------------------------------------------------------------------
 * Cette page affiche la politique de confidentialité / protection
 * des données du site Marché Deluxe.
 *
 * OBJECTIF
 * ----------------------------------------------------------------------
 * Informer les utilisateurs sur :
 * - les données collectées
 * - l'usage de ces données
 * - les moyens de contact
 * - les droits liés à la protection des données
 *
 * PORTÉE
 * ----------------------------------------------------------------------
 * Cette version est volontairement claire, structurée et compatible
 * avec l'architecture actuelle du site.
 *
 * Si tu souhaites une version juridique plus poussée
 * (cookies détaillés, durée de conservation précise, base légale,
 * hébergement, analytics, newsletter, sous-traitants, etc.),
 * elle pourra être enrichie ensuite sans changer la structure.
 */

const sectionStyle = {
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 10px 28px rgba(0,0,0,0.05)",
};

const titleStyle = {
  marginTop: 0,
  marginBottom: 18,
  fontSize: "1.35rem",
  fontFamily: "serif",
  color: "#1f1a17",
};

const textBlockStyle = {
  display: "grid",
  gap: 16,
  fontSize: 15,
  lineHeight: 1.8,
  color: "#312a25",
};

const PolitiqueConfidentialiteView = () => {
  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "40px 20px 80px",
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2.2rem",
            lineHeight: 1.15,
            fontFamily: "serif",
            color: "#1f1a17",
          }}
        >
          Politique de confidentialité
        </h1>

        <p
          style={{
            marginTop: 12,
            fontSize: 15,
            lineHeight: 1.7,
            color: "#4b433d",
            maxWidth: 860,
          }}
        >
          Cette page explique comment les données personnelles sont collectées,
          utilisées et protégées dans le cadre de l’utilisation du site Marché
          Deluxe.
        </p>
      </div>

      {/* IDENTITÉ DU RESPONSABLE */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Responsable du traitement</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Le responsable du traitement des données liées au site Marché Deluxe
            est :
          </p>

          <p style={{ margin: 0, fontWeight: 700 }}>
            La Maison du Fromage Neuchâtel - Sterchi Sàrl
            <br />
            rue du Trésor 2 bis
            <br />
            2000 Neuchâtel
            <br />
            Suisse
          </p>

          <p style={{ margin: 0 }}>
            Contact :
            <br />
            Téléphone : +41 32 725 26 36
            <br />
            Email : neuch@sterchi-fromages.ch
          </p>
        </div>
      </section>

      {/* DONNÉES COLLECTÉES */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Données collectées</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Dans le cadre de l’utilisation du site et de la passation de
            commandes, certaines données peuvent être collectées, notamment :
          </p>

          <div style={{ paddingLeft: 18 }}>
            <p style={{ margin: 0 }}>
              - prénom et nom
              <br />- adresse email
              <br />- numéro de téléphone
              <br />- adresse de livraison ou de facturation
              <br />- informations liées à la commande
              <br />- informations nécessaires au retrait ou à la livraison
            </p>
          </div>

          <p style={{ margin: 0 }}>
            Ces données sont fournies directement par l’utilisateur au moment de
            la création du compte, de la commande ou lors d’un échange avec la
            boutique.
          </p>
        </div>
      </section>

      {/* FINALITÉ */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Utilisation des données</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Les données collectées sont utilisées uniquement dans le cadre de
            l’activité de la boutique et notamment pour :
          </p>

          <div style={{ paddingLeft: 18 }}>
            <p style={{ margin: 0 }}>
              - traiter et suivre les commandes
              <br />- organiser la préparation, le retrait ou la livraison
              <br />- contacter le client au sujet de sa commande
              <br />- assurer le bon fonctionnement de l’espace client
              <br />- répondre aux demandes envoyées à la boutique
              <br />- respecter les obligations comptables, administratives et
              légales
            </p>
          </div>
        </div>
      </section>

      {/* PAIEMENT */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Paiement</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Les paiements en ligne sont traités via un prestataire de paiement
            externe sécurisé. Les informations de carte bancaire ne sont pas
            stockées directement sur le site Marché Deluxe.
          </p>

          <p style={{ margin: 0 }}>
            Lorsqu’un paiement en ligne est utilisé, certaines informations
            nécessaires à la transaction sont transmises au prestataire de
            paiement concerné, dans le strict cadre du traitement du paiement.
          </p>
        </div>
      </section>

      {/* CONSERVATION */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Conservation des données</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Les données sont conservées pendant la durée nécessaire au
            traitement de la relation client, au suivi des commandes et au
            respect des obligations légales ou administratives applicables.
          </p>

          <p style={{ margin: 0 }}>
            Certaines données peuvent être conservées plus longtemps lorsqu’une
            obligation comptable, fiscale ou légale l’exige.
          </p>
        </div>
      </section>

      {/* CONFIDENTIALITÉ */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Confidentialité et sécurité</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Les données personnelles sont traitées de manière confidentielle et
            avec un niveau de sécurité approprié compte tenu de la nature des
            informations traitées.
          </p>

          <p style={{ margin: 0 }}>
            L’accès aux données est limité aux personnes qui en ont besoin dans
            le cadre de l’exploitation du service, du traitement des commandes
            et du support client.
          </p>
        </div>
      </section>

      {/* DROITS */}

      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2 style={titleStyle}>Droits des utilisateurs</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Toute personne concernée peut demander des informations sur les
            données la concernant et solliciter, selon le cas, leur correction
            ou leur mise à jour.
          </p>

          <p style={{ margin: 0 }}>
            Pour toute demande liée à la protection des données, il est possible
            de contacter directement :
          </p>

          <p style={{ margin: 0, fontWeight: 700 }}>
            neuch@sterchi-fromages.ch
            <br />
            +41 32 725 26 36
          </p>
        </div>
      </section>

      {/* MISE À JOUR */}

      <section style={sectionStyle}>
        <h2 style={titleStyle}>Modification de la politique</h2>

        <div style={textBlockStyle}>
          <p style={{ margin: 0 }}>
            Cette politique de confidentialité peut être mise à jour à tout
            moment afin de refléter l’évolution du site, des services proposés
            ou des obligations applicables.
          </p>

          <p style={{ margin: 0 }}>
            La version publiée sur cette page est celle faisant foi au moment de
            la consultation.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PolitiqueConfidentialiteView;
